import axios from "axios";

import { ENDPOINTS } from "@/constants/global/endpoints";

const api = axios.create({
    headers: { "Content-Type": "application/json" },
});

// Attach the access token to every request
api.interceptors.request.use((config) => {
    const token = localStorage.getItem("access_token");
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// --- Token refresh queue ---
let isRefreshing = false;
let failedQueue: Array<{
    resolve: (token: string) => void;
    reject: (err: unknown) => void;
}> = [];

function processQueue(error: unknown, token: string | null = null) {
    failedQueue.forEach((p) => (error ? p.reject(error) : p.resolve(token!)));
    failedQueue = [];
}

// Auto-refresh on 401: queue requests while a refresh is in flight
api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        if (error.response?.status !== 401 || originalRequest._retry) {
            return Promise.reject(error);
        }

        const refreshToken = localStorage.getItem("refresh_token");
        if (!refreshToken) {
            return Promise.reject(error);
        }

        if (isRefreshing) {
            return new Promise<string>((resolve, reject) => {
                failedQueue.push({ resolve, reject });
            }).then((token) => {
                // Marked before the replay, exactly as the request that triggered the
                // refresh marks itself below: without it, a queued request whose
                // replay also comes back 401 falls through to this branch again and
                // starts a second refresh cycle instead of being rejected.
                originalRequest._retry = true;
                originalRequest.headers.Authorization = `Bearer ${token}`;
                return api(originalRequest);
            });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
            const { data } = await axios.post(ENDPOINTS.tokenRefresh, {
                refresh: refreshToken,
            });
            const newAccess: string = data.access;
            localStorage.setItem("access_token", newAccess);
            // The backend rotates the refresh token on every use (see
            // POST /auth/token/refresh/) — storing only the access token here left
            // the original refresh token in place, so a session that refreshed
            // continuously still hit a hard wall exactly 7 days after login instead
            // of rolling forward with activity. Guarded on truthiness so a backend
            // that ever stops rotating doesn't wipe out the still-valid stored token.
            if (data.refresh) {
                localStorage.setItem("refresh_token", data.refresh);
            }
            processQueue(null, newAccess);
            originalRequest.headers.Authorization = `Bearer ${newAccess}`;
            return api(originalRequest);
        } catch (err) {
            processQueue(err, null);
            localStorage.removeItem("access_token");
            localStorage.removeItem("refresh_token");
            // Same reason as in AuthContext.logout: the default header outlives
            // localStorage, so a dead session would keep sending its bearer.
            delete api.defaults.headers.common.Authorization;
            window.location.href = "/login";
            return Promise.reject(err);
        } finally {
            isRefreshing = false;
        }
    }
);

export default api;
